/**
 * SearchService.js - Enhanced service with robust error handling
 *
 * Improvements:
 * - Better error handling and logging
 * - Network retry logic
 * - Request timeout handling
 * - Connection status monitoring
 * - Fallback mechanisms
 */
import { tokenService } from './auth/TokenService';
import { cacheService } from './CacheService';

class SearchService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.requestTimeout = 10000; // 10 seconds
    // Initialize baseURL
    this.baseURL = this.getApiUrl();
  }

  /**
   * Get the API URL from cacheService (same as SpotlightService)
   */
  getApiUrl() {
    return cacheService.getApiUrl();
  }

  /**
   * Enhanced request with retry logic and better error handling
   * @param {string} url - Request URL
   * @param {Object} options - Fetch options
   * @returns {Promise<Object>} Response data
   */
  async makeRequest(url, options = {}) {
    // Check network connectivity
    if (!navigator.onLine) {
      throw new Error('No internet connection. Please check your network and try again.');
    }

    let lastError = null;

    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        // Get auth headers using the same method as SpotlightService
        let authHeaders = {};
        try {
          authHeaders = await tokenService.getAuthHeaders();
        } catch (authError) {
          console.error(`[SearchService] Auth error:`, authError);
          throw new Error('Authentication failed. Please refresh the page and try again.');
        }

        const headers = {
          'Content-Type': 'application/json',
          ...authHeaders,
          ...options.headers,
        };

        // Create request with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.requestTimeout);

        const fetchOptions = {
          ...options,
          headers,
          signal: controller.signal,
        };

        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error(`[SearchService] API Error:`, errorData);
          } catch (parseError) {
            const errorText = await response.text();
            console.error(`[SearchService] Raw error response:`, errorText);
            if (errorText) {errorMessage = errorText;}
          }

          // Handle specific HTTP status codes
          switch (response.status) {
            case 401:
              throw new Error('Authentication failed. Please refresh the page and try again.');
            case 403:
              throw new Error('Access denied. Please check your API permissions.');
            case 404:
              throw new Error('The requested resource was not found.');
            case 429:
              throw new Error('Too many requests. Please wait a moment and try again.');
            case 500:
            case 502:
            case 503:
            case 504:
              // Server errors - retry
              if (attempt < this.retryAttempts) {
                await this.delay(this.retryDelay * attempt);
                continue;
              }
              throw new Error('Server error. Please try again later.');
            default:
              throw new Error(errorMessage);
          }
        }

        const data = await response.json();

        return data;
      } catch (error) {
        lastError = error;
        console.error(`[SearchService] Attempt ${attempt} failed:`, error);

        // Handle specific error types
        if (error.name === 'AbortError') {
          throw new Error('Request timeout. Please check your connection and try again.');
        }

        if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
          // Network error - could be CORS, DNS, or connectivity
          if (attempt < this.retryAttempts) {
            await this.delay(this.retryDelay * attempt);
            continue;
          }
          throw new Error('Network error. Please check your internet connection and try again.');
        }

        // If it's not a retryable error, throw immediately
        if (attempt === this.retryAttempts || !this.isRetryableError(error)) {
          throw error;
        }

        // Wait before retry
        await this.delay(this.retryDelay * attempt);
      }
    }

    // If we get here, all retries failed
    throw lastError || new Error('Request failed after multiple attempts');
  }

  /**
   * Determine if an error is retryable
   * @param {Error} error - The error to check
   * @returns {boolean} Whether the error is retryable
   */
  isRetryableError(error) {
    const retryableMessages = ['Failed to fetch', 'Network request failed', 'timeout', 'aborted'];

    return retryableMessages.some(msg => error.message.toLowerCase().includes(msg.toLowerCase()));
  }

  /**
   * Delay utility
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Delay promise
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Search for channels with enhanced error handling
   * @param {string} query - Search query
   * @param {string} platform - Platform identifier
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchChannels(query, platform = 'website', options = {}) {
    if (!query || query.trim().length === 0) {
      return { channels: [], total: 0 };
    }

    const { from = 0, size = 20, excludeRatings = '', dma = '', market = '', signal } = options;

    const cacheKey = `channels-${query}-${platform}-${JSON.stringify({
      from,
      size,
      excludeRatings,
      dma,
      market,
    })}`;

    // Return cached result if available
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Return pending request if already in progress
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const searchParams = new URLSearchParams({
      q: query.trim(),
      from: from.toString(),
      size: size.toString(),
    });

    if (excludeRatings) {searchParams.append('exclude_ratings', excludeRatings);}
    if (dma) {searchParams.append('dma', dma);}
    if (market) {searchParams.append('market', market);}
    searchParams.append('include_child_info', 'true');

    const url = `${this.getApiUrl()}/find/channels/${encodeURIComponent(platform)}?${searchParams}`;

    const requestOptions = {};
    if (signal) {
      requestOptions.signal = signal;
    }

    const requestPromise = this.makeRequest(url, requestOptions)
      .then(data => {
        // Validate response structure
        if (typeof data !== 'object' || data === null) {
          throw new Error('Invalid response format from server');
        }

        // Ensure required fields exist
        const result = {
          channels: Array.isArray(data.channels) ? data.channels : [],
          total: typeof data.total === 'number' ? data.total : 0,
          success: data.success !== false,
        };

        this.cache.set(cacheKey, result);
        this.pendingRequests.delete(cacheKey);

        return result;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        console.error(`[SearchService] Search failed:`, error);
        throw error;
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Get autocomplete suggestions with enhanced error handling
   * @param {string} query - Partial search query
   * @param {Object} options - Request options
   * @returns {Promise<Object>} Autocomplete suggestions
   */
  async getChannelSuggestions(query, options = {}) {
    if (!query || query.length < 2) {
      return { channels: [] };
    }

    const cacheKey = `suggestions-channels-${query.trim()}`;

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const url = `${this.getApiUrl()}/find/channels/autocomplete?q=${encodeURIComponent(
      query.trim(),
    )}`;

    const requestOptions = {};
    if (options.signal) {
      requestOptions.signal = options.signal;
    }

    const requestPromise = this.makeRequest(url, requestOptions)
      .then(data => {
        // Validate response structure
        const result = {
          channels: Array.isArray(data.channels) ? data.channels : [],
          success: data.success !== false,
        };

        this.cache.set(cacheKey, result);
        this.pendingRequests.delete(cacheKey);

        return result;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        console.error(`[SearchService] Suggestions failed:`, error);
        throw error;
      });

    this.pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /**
   * Get channel details by ID or slug
   * @param {string} channelId - The ID or slug of the channel
   * @param {string} countryCode - The country code (e.g., 'US')
   * @returns {Promise<Object>} Channel details
   */
  async getChannelDetails(channelId, countryCode = 'US') {
    if (!channelId) {
      throw new Error('Channel ID is required to fetch details.');
    }

    const cacheKey = `channel-details-${channelId}-${countryCode}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Use the same API URL approach as SpotlightService
    const apiUrl = this.getApiUrl();
    const url = `${apiUrl}/channel/${countryCode}/${channelId}`;

    try {
      const data = await this.makeRequest(url);
      if (!data || !data.channels || data.channels.length === 0) {
        throw new Error('No channel data found.');
      }
      const channel = data.channels[0];
      this.cache.set(cacheKey, channel);
      return channel;
    } catch (error) {
      console.error(`[SearchService] Failed to fetch channel details for ${channelId}:`, error);
      throw error;
    }
  }

  /**
   * Clear cache with pattern matching
   * @param {string} pattern - Optional pattern to match cache keys
   */
  clearCache(pattern = null) {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get service health status
   * @returns {Object} Service health information
   */
  getHealthStatus() {
    return {
      baseURL: this.baseURL,
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      isOnline: navigator.onLine,
      retryAttempts: this.retryAttempts,
      requestTimeout: this.requestTimeout,
    };
  }

  /**
   * Test API connectivity
   * @returns {Promise<boolean>} Whether API is accessible
   */
  async testConnectivity() {
    try {
      await this.makeRequest(`${this.getApiUrl()}/find/channels/autocomplete?q=test`, {
        method: 'GET',
      });
      return true;
    } catch (error) {
      console.error(`[SearchService] Connectivity test failed:`, error);
      return false;
    }
  }
}

export default new SearchService();
